import { useEffect, useState } from 'react';
import { copyToClipboard, shareTargets } from '@/lib/share';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  url: string;
  title: string;
}

export default function ShareDialog({ open, onClose, url, title }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
  };

  const openShare = (key: string) => {
    const target = shareTargets.find((t) => t.key === key);
    if (!target) return;
    window.open(target.buildUrl(url, title), '_blank', 'noopener,noreferrer,width=640,height=560');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-lg bg-background-50 border border-background-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">分享内容</h3>
            <p className="mt-1 text-xs text-foreground-500">复制链接或分享到社交平台（前端演示）</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
            aria-label="关闭分享"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 p-2">
          <input
            readOnly
            value={url}
            className="flex-1 bg-transparent px-2 text-xs text-foreground-700 focus:outline-none"
            aria-label="分享链接"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary-500 font-label text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true" />
            {copied ? '已复制' : '复制'}
          </button>
        </div>

        <div className="mt-5">
          <p className="font-label text-xs font-semibold text-foreground-600">分享到</p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {shareTargets.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => openShare(t.key)}
                className="flex flex-col items-center gap-2 rounded-md border border-background-200 py-3 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className={`${t.icon} text-xl`} aria-hidden="true" />
                <span className="font-label text-xs">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
          提示：社交平台分享为前端模拟跳转，未接入真实分享接口。
        </p>
      </div>
    </div>
  );
}